import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, isValidElement } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { PopoverClose, PopoverContent } from '@/components/ui/popover';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { cn } from '@/lib/cn';

/**
 * FilterChip用のPopoverContentラッパー
 *
 * @remarks
 * FilterChipコンポーネントで使用するPopoverContentの共通実装。
 * 画面高さに応じた動的な高さ調整とスクロール対応を提供します。
 *
 * ## 特徴
 * - **動的な高さ調整**: Radix UIのCSSカスタムプロパティ `--radix-popover-content-available-height` を使用
 * - **画面端からのパディング**: `collisionPadding={20}` で画面端から20pxのパディングを確保
 * - **Flexboxレイアウト**: `flex flex-col` でコンテンツを可変領域に配置
 * - **スクロール対応**: 子要素を `flex-1 overflow-hidden` でラップしてスクロール可能に
 *
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <FilterChip type="available" iconName="AppsIcon">
 *       ソフトウェア
 *     </FilterChip>
 *   </PopoverTrigger>
 *   <FilterChipPopoverContent>
 *     <Combobox
 *       type="multiple"
 *       options={options}
 *       value={selectedIds}
 *       onValueChange={setSelectedIds}
 *     />
 *   </FilterChipPopoverContent>
 * </Popover>
 * ```
 */
export const FilterChipPopoverContent = forwardRef<HTMLDivElement, Omit<ComponentPropsWithoutRef<typeof PopoverContent>, 'children'> & {
  children: ReactNode;
  /**
   * 適用/キャンセルのフッター表示を明示制御する override。
   * 未指定時は直接の子 Combobox の `type` から自動判定し、`type="multiple"` の時だけ表示する
   * （単一選択はクリックで即適用・クローズするため確定ボタンは不要）。
   */
  showApplyButton?: boolean;
  /** キャンセル（適用せず閉じて選択を破棄）ハンドラ。渡した場合のみキャンセルボタンを表示 */
  onCancel?: () => void;
}>(({
  children,
  className,
  showApplyButton,
  onCancel,
  ...props
}, ref) => {
  const tc = useCommonTranslation();

  // 確定手段が必要なのは複数選択のみ。determinant は子 Combobox の type。
  const childIsMultiple = isValidElement<{
    type?: 'multiple' | 'single';
  }>(children) && children.props.type === 'multiple';
  const showFooter = showApplyButton ?? childIsMultiple;
  return <PopoverContent ref={ref} className={cn('w-popover-xl p-0', 'max-h-(--radix-popover-content-available-height)', 'flex flex-col', className)} collisionPadding={20} {...props}>
      <div className={cn('flex-1 min-h-0 flex flex-col overflow-hidden', '[&_[cmdk-root]]:flex-1 [&_[cmdk-root]]:h-auto [&_[cmdk-root]]:min-h-0', '[&_[cmdk-list]]:max-h-none [&_[cmdk-list]]:flex-1 [&_[cmdk-list]]:min-h-0')}>
        {children}
      </div>
      {showFooter && <div className="flex gap-2 border-t p-2">
          {onCancel && <ActionButton variant="neutral" size="sm" onClick={onCancel}>
              {tc('action.cancel')}
            </ActionButton>}
          <PopoverClose asChild>
            <ActionButton className="ml-auto" variant="default" size="sm">
              {tc('action.apply')}
            </ActionButton>
          </PopoverClose>
        </div>}
    </PopoverContent>;
});
FilterChipPopoverContent.displayName = 'FilterChipPopoverContent';